// Compact BuildType controls for the header strip.
//
// A slimmer version of BuildTypeField — no T2Label (header supplies that
// via <Field label="Build">), narrower dropdown, and the CS/Hub popover
// buttons appear inline next to it.
//
// Cross-sell content + popover shells are re-used from BuildTypeField.

import { useState } from "react";
import { Sparkles, BookOpen, X } from "lucide-react";
import { BUILD_TYPES, CROSS_SELL_MAP } from "./_buildTypeContent";

export default function BuildTypeControls({ value, onChange, testidPrefix = "urgency" }) {
  const [otherLocal, setOtherLocal] = useState("");

  const matched = BUILD_TYPES.find((b) => b.label === value || b.key === value);
  const isOther = !matched || matched.key === "other";
  const currentKey = matched?.key || (value ? "other" : "");

  const handleSelect = (key) => {
    const entry = BUILD_TYPES.find((b) => b.key === key);
    if (!entry) { onChange(""); return; }
    if (key === "other") onChange(otherLocal || entry.label);
    else { onChange(entry.label); setOtherLocal(""); }
  };

  const rawOtherText = isOther && !matched ? value : "";

  return (
    <>
      <select
        className="t2-input t2-mono"
        value={currentKey}
        onChange={(e) => handleSelect(e.target.value)}
        data-testid="quote-buildtype"
        data-empty={currentKey ? "false" : "true"}
        style={{ padding: "4px 10px", fontSize: 13.5, minWidth: 230, height: 34 }}
      >
        <option value="">— Select —</option>
        {BUILD_TYPES.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
      </select>

      {isOther && currentKey === "other" && (
        <input
          className="t2-input"
          style={{ padding: "4px 10px", fontSize: 13.5, minWidth: 170, height: 34 }}
          placeholder="Describe…"
          defaultValue={rawOtherText || otherLocal}
          onChange={(e) => setOtherLocal(e.target.value)}
          onBlur={() => { if (otherLocal) onChange(otherLocal); }}
          data-testid="quote-buildtype-other"
        />
      )}

    </>
  );
}


// CS (cross-sell) + HUB reference buttons — shown in the CW Staff Notes header.
export function BuildTypeHelp({ value }) {
  const [showCS, setShowCS]   = useState(false);
  const [showHub, setShowHub] = useState(false);
  const matched = BUILD_TYPES.find((b) => b.label === value || b.key === value);
  const csEntry = CROSS_SELL_MAP[matched?.key || (value ? "other" : "")] || CROSS_SELL_MAP.other;
  return (
    <>
      <button
        type="button"
        onClick={() => setShowCS(true)}
        data-testid="build-type-cross-sell-btn"
        title="Cross-sell guide — buzzwords, upsells, script lines"
        style={{
          display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 7px",
          fontSize: 11, background: "rgba(219, 96, 82, 0.10)",
          color: "var(--t2-red-ink)", border: "1px solid rgba(219, 96, 82, 0.35)",
          borderRadius: 4, cursor: "pointer", fontFamily: "var(--t2-mono)",
          textTransform: "uppercase", letterSpacing: ".05em", height: 24,
        }}
      >
        <Sparkles className="w-3 h-3" /> CS
      </button>
      <button
        type="button"
        onClick={() => setShowHub(true)}
        data-testid="build-type-hub-btn"
        title="Reference Hub — tips & tricks"
        style={{
          display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 7px",
          fontSize: 11, background: "rgba(47, 200, 203, 0.10)",
          color: "var(--t2-teal-ink)", border: "1px solid rgba(47, 200, 203, 0.35)",
          borderRadius: 4, cursor: "pointer", fontFamily: "var(--t2-mono)",
          textTransform: "uppercase", letterSpacing: ".05em", height: 24,
        }}
      >
        <BookOpen className="w-3 h-3" /> HUB
      </button>

      {showCS  && <CrossSellPopover entry={csEntry} label={matched?.label || "Any build"} onClose={() => setShowCS(false)} />}
      {showHub && <HubReferencePopover onClose={() => setShowHub(false)} />}
    </>
  );
}


// ─── Popovers (same shell as BuildTypeField, but self-contained) ───────
function CrossSellPopover({ entry, label, onClose }) {
  return (
    <PopoverShell testid="cross-sell-popover" title={`Cross-sell guide · ${label}`} tone="red" onClose={onClose}>
      <Section title="Upsell candidates" tone="var(--t2-red-ink)">
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
          {entry.upsells.map((u, i) => <li key={i} style={{ marginBottom: 4 }}>{u}</li>)}
        </ul>
      </Section>
      <Section title="Buzzwords · trade credibility" tone="var(--t2-teal-ink)">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {entry.buzzwords.map((b, i) => (
            <span key={i}
              style={{
                fontSize: 10.5, padding: "2px 8px", borderRadius: 12,
                background: "rgba(47, 200, 203, 0.10)", color: "var(--t2-teal-ink)",
                border: "1px solid rgba(47, 200, 203, 0.30)",
                fontFamily: "var(--t2-mono)",
              }}
            >{b}</span>
          ))}
        </div>
      </Section>
      <Section title="Script lines · say verbatim" tone="var(--t2-warn-ink)">
        <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.55 }}>
          {entry.scripts.map((s, i) => (
            <li key={i} style={{ marginBottom: 8, color: "var(--t2-grey1)", fontStyle: "italic" }}>{s}</li>
          ))}
        </ol>
      </Section>
    </PopoverShell>
  );
}
function HubReferencePopover({ onClose }) {
  return (
    <PopoverShell testid="hub-reference-popover" title="Reference Hub · tips & tricks" tone="teal" onClose={onClose}>
      <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--t2-grey1)", lineHeight: 1.55 }}>
        The Hub houses the full library of tips, tricks, product deep-dives and historic customer
        stories — anything a rep might need to sound like the expert in the room.
      </p>
      <a href="/hub" target="_blank" rel="noopener noreferrer" data-testid="hub-reference-open"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "8px 14px", borderRadius: 6,
          background: "var(--t2-teal)", color: "var(--t2-ink)",
          fontSize: 12, fontWeight: 700, textDecoration: "none",
          fontFamily: "var(--t2-mono)", textTransform: "uppercase", letterSpacing: ".06em",
        }}
      ><BookOpen className="w-3.5 h-3.5" /> Open Hub in new tab</a>
    </PopoverShell>
  );
}
function PopoverShell({ title, tone, onClose, children, testid }) {
  const border = tone === "red" ? "rgba(219, 96, 82, 0.40)"
               : tone === "teal" ? "rgba(47, 200, 203, 0.40)"
               : "rgba(255, 255, 255, 0.15)";
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 60,
      background: "rgba(0, 0, 0, 0.55)",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "8vh 20px",
    }} onClick={onClose} data-testid={testid}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "min(560px, 100%)", maxHeight: "80vh", overflowY: "auto",
        background: "var(--t2-ink)", border: `1px solid ${border}`,
        borderRadius: 10, padding: 20, boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
      }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: "var(--t2-grey1)", fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{
            marginLeft: "auto", padding: 4, background: "none",
            border: "none", color: "var(--t2-grey3)", cursor: "pointer",
          }} data-testid={`${testid}-close`}><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
function Section({ title, tone, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 9.5, textTransform: "uppercase", letterSpacing: ".08em",
        color: tone || "var(--t2-grey3)", fontFamily: "var(--t2-mono)", marginBottom: 6,
      }}>{title}</div>
      <div style={{ fontSize: 12.5, color: "var(--t2-grey2)" }}>{children}</div>
    </div>
  );
}
